import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { bulkUploadI18n } from '/business/shared/util/translitKeys';
import type { QuestionInputBulkUploadKeys, QuestionInputField } from '/index';
import type { QuestionTreeItem } from '/business/shared/functions/types';
import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  FIELD_TYPE_DROPDOWN,
  FIELD_TYPE_TXT,
} from '/business/shared/components/questions/helper/fieldEnums';
import { POST_HEADER, STANDARD } from '/business/shared/components/pages/question/questionEnums';
import getApplicationCommuteWorkforceSelector from '/business/shared/state/selectors/application/getApplicationCommuteWorkforceSelector';
import getAccurateEmployeeCommutesFromXlsx from '/business/shared/functions/xlsx/getAccurateEmployeeCommutesFromXlsx';
import EmployeeCommutesWorkforceInfoTip from '/business/shared/components/questions/scope3/employeeCommutes/EmployeeCommutesWorkforceInfoTip';
import OverviewGroupContentParagraph from '/shared/components/widgets/overview/OverviewGroupContentParagraph';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledFormLabel from '/shared/styled/form/StyledFormLabel';
import StyledFormLabelSubtext from '/shared/styled/form/StyledFormLabelSubtext';
import useGetDefaultDistanceUnit, { type DefaultDistanceUnit } from '/business/shared/hooks/useGetDefaultDistanceUnit';
import useGetSharedEmployeeCommuteValues, { type CommuteFactor } from './useGetSharedEmployeeCommuteValues';
type UseEmployeeCommutesAccurateSchemaProps = {
  commuterFactors?: CommuteFactor[];
  isEditingItem?: boolean;
  question: QuestionTreeItem;
};
type UseEmployeeCommutesAccurateSchemaResult = {
  pageSchema: QuestionInputField[];
  bulkUploadI18nKeys: QuestionInputBulkUploadKeys;
  sidebar: ReactNode;
  allowBulkUpload: boolean;
  formDisabled: boolean;
  bulkUploadCallback: typeof getAccurateEmployeeCommutesFromXlsx;
  bulkUploadProps: {
    commuterFactors?: CommuteFactor[];
    distances: ReturnType<typeof useGetSharedEmployeeCommuteValues>['distances'];
  };
  isBulkUploadModeToggled: boolean | null;
  setIsBulkUploadModeToggled: Dispatch<SetStateAction<boolean | null>>;
};
export default function useEmployeeCommutesAccurateSchema({
  commuterFactors,
  isEditingItem = false,
  question,
}: UseEmployeeCommutesAccurateSchemaProps): UseEmployeeCommutesAccurateSchemaResult {
  const { t } = useTranslation();
  const [valueDistanceUnit, setValueDistanceUnit] = useState<DefaultDistanceUnit | null>(null);
  const { distanceUnit } = useGetDefaultDistanceUnit();
  const [isBulkUploadModeToggled, setIsBulkUploadModeToggled] = useState<boolean | null>(null);
  const { hasCommuteWorkforce } = useSelector(getApplicationCommuteWorkforceSelector);
  const { distances, commuterFactorValues, distanceUnitValues, distanceValues, employmentTypeField } =
    useGetSharedEmployeeCommuteValues({
      commuterFactors,
      valueDistanceUnit,
      commuterFactorDefaultI18nKey: 'business:QuestionPage.forms.commutesAccurate.selectTransportMode',
      distanceDefaultI18nKey: 'business:QuestionPage.forms.commutesAccurate.selectDistance',
    });
  useEffect(() => {
    if (valueDistanceUnit === null) {
      setValueDistanceUnit(distanceUnit);
    }
  }, [distanceUnit, valueDistanceUnit]);
  const firstName: QuestionInputField = useMemo(() => {
    return {
      propName: 'firstName',
      element: 'first_name',
      type: FIELD_TYPE_TXT,
      placeholderI18nKey: 'business:QuestionPage.forms.commutesAccurate.firstNamePlaceholder',
      labelI18nKey: 'business:QuestionPage.forms.commutesAccurate.para5',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOIDENTIFIER',
          errStr: t('business:Errors.transport.provideEmployeeFirstName'),
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRNUMBEROFUNITSTOOLONG',
          errStr: t('business:Errors.transport.employeeFirstNameTooLong'),
          value: 3000,
        },
      ],
    };
  }, [t]);
  const commuterFactor: QuestionInputField = useMemo(() => {
    return {
      propName: 'commuterFactor',
      type: FIELD_TYPE_DROPDOWN,
      element: 'commuter_factor',
      labelI18nKey: 'business:QuestionPage.forms.commutesAccurate.para6',
      values: commuterFactorValues,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOCOMMUTERFACTOR',
          errStr: t('business:Errors.transport.provideType'),
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRCOMMUTERFACTORNOTFOUND',
          errStr: t('business:Errors.transport.notFound'),
        },
      ],
    };
  }, [commuterFactorValues, t]);
  const distance: QuestionInputField = useMemo(() => {
    return {
      propName: 'distance',
      type: FIELD_TYPE_DROPDOWN,
      values: distanceValues,
      element: 'distance',
      // the label copy carries its own asterisk; suppress the auto one
      titleHasAsterisk: false,
      label: (
        <Trans
          i18nKey="business:QuestionPage.forms.commutesAccurate.para7"
          components={{
            1: <StyledFormLabelSubtext $noMargin>{null}</StyledFormLabelSubtext>,
          }}
        />
      ),
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNODISTANCE',
          errStr: t('business:Errors.transport.provideDistance'),
        },
      ],
      dropdown: {
        propName: 'distanceUnit',
        element: 'distance_unit',
        defaultValue: distanceUnit,
        values: distanceUnitValues,
        onChangeCallback: (value) => {
          if (value === 'km' || value === 'miles') {
            setValueDistanceUnit(value);
          }
        },
      },
    };
  }, [distanceUnitValues, distanceUnit, distanceValues, t]);
  const injectedFields: QuestionInputField = useMemo(() => {
    return {
      grouping: POST_HEADER,
      reactElement: (
        <>
          {!isEditingItem && (
            <>
              <StyledFormLabel as="div">
                <StyledHeading as="div" $type="h2">
                  {/* @i18n: Do you want to add employee commute data manually or bulk upload with a spreadsheet[...] */}
                  <Trans i18nKey="business:QuestionPage.forms.commutesAccurate.para1" />
                </StyledHeading>
                <OverviewGroupContentParagraph as="div">
                  <p>
                    {/* @i18n: We recommend bulk upload for more than 10 employees. */}
                    <Trans i18nKey="business:QuestionPage.forms.commutesAccurate.para2" />
                  </p>
                </OverviewGroupContentParagraph>
                <StyledInfoFlash>
                  <div>
                    {/* @i18n: For more information, refer to the "Help" section for detailed guidance. */}
                    <Trans i18nKey="business:QuestionPage.forms.commutesAccurate.infoFlash" />
                  </div>
                </StyledInfoFlash>
              </StyledFormLabel>
            </>
          )}
        </>
      ),
    };
  }, [isEditingItem]);
  const injectedFields2: QuestionInputField = useMemo(() => {
    return {
      grouping: STANDARD,
      reactElement: (
        <>
          {!isEditingItem && isBulkUploadModeToggled === false && (
            <>
              <StyledFormLabel $moreBottomPadding as="div">
                <StyledHeading as="div" $type="h2">
                  {/* @i18n: Enter employee commute details for each employee, including how far they travel and[...] */}
                  <Trans i18nKey="business:QuestionPage.forms.commutesAccurate.para3" />
                </StyledHeading>
              </StyledFormLabel>
              <StyledInfoFlash $info>
                <div>
                  {/* @i18n: If an employee commutes in a company vehicle, do not enter the data here. Enter it in[...] */}
                  <Trans i18nKey="business:QuestionPage.forms.commutesAccurate.para4" />
                </div>
              </StyledInfoFlash>
            </>
          )}
        </>
      ),
    };
  }, [isEditingItem, isBulkUploadModeToggled]);
  const pageSchema: QuestionInputField[] = useMemo(() => {
    const fields = [injectedFields, injectedFields2, firstName, employmentTypeField, commuterFactor, distance];
    if (hasCommuteWorkforce) {
      return fields;
    }
    return fields.map((field) => ({ ...field, isDisabled: true }));
  }, [hasCommuteWorkforce, injectedFields, injectedFields2, firstName, employmentTypeField, commuterFactor, distance]);
  const bulkUploadI18nKeys: QuestionInputBulkUploadKeys = useMemo(() => {
    return {
      [bulkUploadI18n.bulkUploadTemplateFileName]: 'business:QuestionPage.forms.commutesAccurate.bulkUploadFilename',
      [bulkUploadI18n.stepOneHeadingDownloadTemplate]: 'business:QuestionPage.forms.commutesAccurate.para8',
      [bulkUploadI18n.stepOneExplainer]: 'business:QuestionPage.forms.commutesAccurate.para9',
      [bulkUploadI18n.stepTwoHeadingFillOutTemplate]: 'business:QuestionPage.forms.commutesAccurate.para10',
      [bulkUploadI18n.stepThreeHeadingUpload]: 'business:QuestionPage.forms.commutesAccurate.para11',
      [bulkUploadI18n.stepThreeExplainer]: 'business:QuestionPage.forms.commutesAccurate.para12',
      [bulkUploadI18n.stepThreeFileTypeRestrictions]: 'business:QuestionPage.forms.commutesAccurate.para13',
      [bulkUploadI18n.viewUploadedItems]: 'business:QuestionPage.forms.commutesAccurate.viewUploadedEmployees',
    };
  }, []);
  const bulkUploadProps = useMemo(() => {
    return {
      commuterFactors,
      distances: distances,
    };
  }, [distances, commuterFactors]);
  return {
    pageSchema,
    bulkUploadI18nKeys,
    sidebar: isEditingItem ? null : <EmployeeCommutesWorkforceInfoTip question={question} />,
    allowBulkUpload: true,
    formDisabled: !hasCommuteWorkforce,
    bulkUploadCallback: getAccurateEmployeeCommutesFromXlsx,
    bulkUploadProps,
    isBulkUploadModeToggled,
    setIsBulkUploadModeToggled,
  };
}
